import { useMemo } from 'react';
import { OperatoryDetails } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { ScheduleDefinition } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import { SortOrder } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/sort.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type { AvailabilityMode } from '../../../../components/availability/availability-form/types';

export type AvailabilityRuleEntity = {
  id: string;
  displayName: string;
  externalName: string;
  sourceId: string;
  operatoryIcon?: OperatoryDetails['icon'];
  scheduleDefinitions: ScheduleDefinition[];
  isActive?: boolean;
  count: number;
};

export const useGetAvailabilityRules = ({
  locationId,
  searchValue,
  sourceIds,
  availabilityMode,
}: {
  locationId: string;
  searchValue: string;
  sourceIds?: string[];
  availabilityMode: AvailabilityMode;
}) => {
  const query = SchedulerV3.Queries.useListScheduleDefinitionsQuery({
    request: {
      availabilityLens: availabilityMode,
      locationId,
      name: searchValue,
      ...(sourceIds?.length && { sourceIds }),
      sort: { sortBy: 'name', sortDir: SortOrder.ASC },
    },
  });

  const transformedData: AvailabilityRuleEntity[] = useMemo(() => {
    if (availabilityMode === 'OPERATORY') {
      return (
        query.data?.operatoryGroups?.map((group) => ({
          id: group.operatoryId,
          displayName: group.operatoryDisplayName,
          externalName: group.operatoryExternalName || '',
          sourceId: group.sourceId,
          operatoryIcon: group.opDetails?.icon,
          scheduleDefinitions: group.scheduleDefinitions,
          isActive: true, // Currently API doesn't return any field to determine active status and all active entities are returned.
          count: group.groupSdCountTotal || 0,
        })) || []
      );
    }
    if (availabilityMode === 'PROVIDER') {
      return (
        query.data?.providerGroups?.map((group) => ({
          id: group.providerId,
          displayName: group.providerDisplayName,
          externalName: group.providerExternalName || '',
          sourceId: group.sourceId,
          scheduleDefinitions: group.scheduleDefinitions,
          isActive: true, // Currently API doesn't return any field to determine active status and all active entities are returned.
          count: group.groupSdCountTotal || 0,
        })) || []
      );
    }
    return [];
  }, [availabilityMode, query.data]);

  return {
    ...query,
    data: transformedData,
  };
};
